<template>
  <div class="animated fadeIn">
    <!-- Header com Filtros e Ações -->
    <b-card class="border-0 shadow-sm mb-4 bg-dark text-white">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h3 class="mb-0 font-weight-bold">Dashboard Estratégico de Estoque</h3>
          <p class="mb-0 text-white-50">Visão em tempo real baseada em etiquetas (ESTOQUE_ETIQUETA)</p>
        </div>
        <div class="d-flex gap-3 align-items-center">
          <b-button variant="primary" pill @click="carregarDados" class="px-4 shadow-sm">
            <i class="fas fa-sync-alt mr-1" :class="{'fa-spin': carregando}"></i> Atualizar
          </b-button>
        </div>
      </div>
    </b-card>

    <!-- KPIs Rápidos -->
    <b-row class="mb-4">
      <b-col md="3">
        <div class="vibrant-card card-v-lucro shadow-lg h-100">
          <div class="vibrant-card__header">
            <div>
              <span class="vibrant-card__label text-uppercase small font-weight-bold">Peso Total (KG)</span>
              <h2 class="vibrant-card__value">{{ formatNumero(totais.TOTAL_PESO) }}</h2>
            </div>
            <div class="vibrant-card__icon"><i class="fas fa-weight-hanging"></i></div>
          </div>
          <div class="vibrant-card__info mt-2">
            <small>Estoque Disponível</small>
          </div>
        </div>
      </b-col>

      <b-col md="3">
        <div class="vibrant-card card-v-receber shadow-lg h-100">
          <div class="vibrant-card__header">
            <div>
              <span class="vibrant-card__label text-uppercase small font-weight-bold">Total de Peças</span>
              <h2 class="vibrant-card__value">{{ formatInteiro(totais.TOTAL_PECAS) }}</h2>
            </div>
            <div class="vibrant-card__icon"><i class="fas fa-boxes"></i></div>
          </div>
          <div class="vibrant-card__info mt-2">
            <small>Volumes em Etiquetas</small>
          </div>
        </div>
      </b-col>

      <b-col md="3">
        <div class="vibrant-card card-v-recebido shadow-lg h-100">
          <div class="vibrant-card__header">
            <div>
              <span class="vibrant-card__label text-uppercase small font-weight-bold">Custo Total (R$)</span>
              <h2 class="vibrant-card__value">{{ formatBRL(totais.CUSTO_TOTAL) }}</h2>
            </div>
            <div class="vibrant-card__icon"><i class="fas fa-dollar-sign"></i></div>
          </div>
          <div class="vibrant-card__info mt-2">
            <small>Valoração de Estoque</small>
          </div>
        </div>
      </b-col>

      <b-col md="3">
        <div class="vibrant-card card-v-pago shadow-lg h-100" @click="abrirDetalhesValidade('VENCIDA')">
          <div class="vibrant-card__header">
            <div>
              <span class="vibrant-card__label text-uppercase small font-weight-bold">Etiquetas Vencidas</span>
              <h2 class="vibrant-card__value">{{ formatInteiro(totais.ETIQUETAS_VENCIDAS) }}</h2>
            </div>
            <div class="vibrant-card__icon"><i class="fas fa-exclamation-triangle"></i></div>
          </div>
          <div class="vibrant-card__info mt-2">
            <small>Alerta de Validade</small>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- Gráficos Principais -->
    <b-row>
      <!-- Gráfico por Grupo -->
      <b-col md="6">
        <b-card class="border-0 shadow-sm mb-4 h-100">
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0 font-weight-bold text-dark"><i class="fas fa-chart-bar mr-2 text-primary"></i>Estoque por Grupo</h5>
              <b-button-group size="sm">
                <b-button :variant="tipoGraficoGrupo === 'PESO' ? 'primary' : 'outline-primary'" @click="tipoGraficoGrupo = 'PESO'">Peso</b-button>
                <b-button :variant="tipoGraficoGrupo === 'PECAS' ? 'primary' : 'outline-primary'" @click="tipoGraficoGrupo = 'PECAS'">Peças</b-button>
              </b-button-group>
            </div>
          </template>
          <div class="chart-container" style="position: relative; height:300px;">
            <canvas ref="chartGrupo"></canvas>
          </div>
        </b-card>
      </b-col>

      <!-- Cronograma de Vencimento -->
      <b-col md="6">
        <b-card class="border-0 shadow-sm mb-4 h-100">
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <h5 class="mb-0 font-weight-bold text-dark"><i class="fas fa-calendar-alt mr-2 text-warning"></i>Cronograma de Vencimento</h5>
              <div style="width: 150px;">
                <cec-input-data v-model="filtroDataLimiteTimeline" @input="carregarDados" />
              </div>
            </div>
          </template>
          <div class="chart-container" style="position: relative; height:300px;">
            <canvas ref="chartTimeline"></canvas>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-row>
      <!-- Gráfico de Validade por Produto -->
      <b-col md="12">
        <b-card class="border-0 shadow-sm mb-4">
          <template #header>
            <h5 class="mb-0 font-weight-bold text-dark"><i class="fas fa-hourglass-half mr-2 text-danger"></i>Situação Crítica de Validade por Produto</h5>
          </template>
          <div class="chart-container" style="position: relative; height:300px;">
            <canvas ref="chartValidade"></canvas>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Tabela de Detalhamento por Grupo -->
    <b-row>
      <b-col md="12">
        <b-card class="border-0 shadow-sm mb-4 bg-soft-gray">
          <template #header>
            <div class="d-flex justify-content-between align-items-center">
              <h6 class="mb-0 font-weight-bold text-dark text-uppercase small">Resumo Detalhado por Grupo</h6>
              <b-button variant="outline-dark" size="sm" @click="imprimirResumoGeral">
                <i class="fas fa-print mr-1"></i> Relatório Geral
              </b-button>
            </div>
          </template>
          
          <div v-for="grupo in porGrupo" :key="grupo.GRUPO" class="mb-3 bg-white rounded shadow-sm border overflow-hidden">
            <div class="p-3 bg-light d-flex justify-content-between align-items-center border-bottom clickable-row" @click="abrirDetalhesGrupo(grupo.GRUPO)">
              <div class="d-flex align-items-center">
                <div class="mr-3 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style="width: 32px; height: 32px;">
                  <i class="fas fa-layer-group small"></i>
                </div>
                <div>
                  <h6 class="mb-0 font-weight-bold">{{ grupo.GRUPO }}</h6>
                  <small class="text-muted">{{ formatInteiro(grupo.PECAS) }} Peças disponíveis</small>
                </div>
              </div>
              <div class="text-right">
                <div class="font-weight-bold text-dark">{{ formatNumero(grupo.PESO) }} KG</div>
                <div class="text-success small font-weight-bold">{{ formatBRL(grupo.CUSTO) }}</div>
              </div>
            </div>
            
            <div class="p-0">
              <table class="table table-sm table-hover mb-0 small">
                <thead class="bg-soft-gray text-muted text-uppercase">
                  <tr>
                    <th style="width: 50px" class="text-center">#</th>
                    <th>Produto</th>
                    <th class="text-center">Peças</th>
                    <th class="text-right">Peso Total</th>
                    <th class="text-right">Custo Est.</th>
                    <th class="text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  <template v-for="(prod, idx) in topProdutosPeso.filter(p => p.GRUPO === grupo.GRUPO)">
                    <tr :key="prod.PRODUTO_ID">
                      <td class="text-center text-muted">{{ idx + 1 }}</td>
                      <td class="font-weight-bold text-primary">{{ prod.PRODUTO }}</td>
                      <td class="text-center">{{ prod.PECAS }}</td>
                      <td class="text-right font-weight-bold">{{ formatNumero(prod.PESO) }} KG</td>
                      <td class="text-right">{{ formatBRL(prod.CUSTO) }}</td>
                      <td class="text-center">
                        <b-button variant="link" size="sm" @click="abrirDetalhesProduto(prod.PRODUTO)" class="p-0">
                          <i class="fas fa-search-plus"></i>
                        </b-button>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Modal de Detalhes Premium -->
    <b-modal
      id="modal-detalhes-estoque"
      size="xl"
      hide-header
      hide-footer
      body-class="p-0 overflow-hidden"
      content-class="border-0 shadow-lg modern-premium-modal"
    >
      <div class="report-header-premium bg-dark text-white p-3 d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center">
          <div class="modal-icon mr-3 bg-primary rounded-circle d-flex align-items-center justify-content-center" style="width: 45px; height: 45px;">
            <i class="fas fa-search-plus"></i>
          </div>
          <div>
            <h5 class="mb-0 font-weight-bold">{{ modalTitulo }}</h5>
            <small class="text-white-50">Análise Detalhada de Itens em Estoque</small>
          </div>
        </div>
        <div class="d-flex align-items-center">
          <b-button variant="outline-light" size="sm" pill class="mr-2 px-3" @click="imprimirDetalhes">
            <i class="fas fa-print mr-1"></i> Imprimir
          </b-button>
          <b-button variant="link" class="text-white p-0" @click="$bvModal.hide('modal-detalhes-estoque')">
            <i class="fas fa-times fa-lg"></i>
          </b-button>
        </div>
      </div>

      <div class="p-4 bg-light" style="max-height: 80vh; overflow-y: auto;">
        <b-row class="mb-4">
          <b-col md="3">
            <div class="kpi-card-premium border-left border-primary">
              <small>Peso Total</small>
              <h3 class="text-primary">{{ formatNumero(detalhesResumo.pesoTotal) }} <span class="small font-weight-normal">KG</span></h3>
            </div>
          </b-col>
          <b-col md="3">
            <div class="kpi-card-premium border-left border-success">
              <small>Total de Peças</small>
              <h3 class="text-success">{{ formatInteiro(detalhesResumo.totalPecas) }}</h3>
            </div>
          </b-col>
          <b-col md="3">
            <div class="kpi-card-premium border-left border-danger">
              <small>Itens Vencidos</small>
              <h3 class="text-danger">{{ formatInteiro(detalhesResumo.itensVencidos) }}</h3>
            </div>
          </b-col>
          <b-col md="3">
            <div class="kpi-card-premium border-left border-info">
              <small>Valor Estimado</small>
              <h3 class="text-info">{{ formatBRL(detalhesResumo.custoTotal) }}</h3>
            </div>
          </b-col>
        </b-row>

        <b-row class="mb-4">
          <b-col md="4">
            <b-card class="h-100 border-0 shadow-sm bg-white">
              <h6 class="font-weight-bold text-muted text-uppercase small mb-3">Distribuição por Status</h6>
              <div style="height: 220px;">
                <canvas ref="chartDonutModal"></canvas>
              </div>
            </b-card>
          </b-col>
          <b-col md="8">
            <div class="bg-white rounded shadow-sm overflow-hidden border">
              <div class="p-2 bg-dark text-white text-uppercase small font-weight-bold px-3">Lista Detalhada de Etiquetas</div>
              <div style="max-height: 250px; overflow-y: auto;">
                <table class="table table-sm table-hover mb-0 small">
                  <thead class="bg-light text-muted">
                    <tr>
                      <th>Cód. Prod</th>
                      <th>Descrição</th>
                      <th class="text-center">Cód. Barras</th>
                      <th class="text-center">Vencimento</th>
                      <th class="text-center">Centro Est.</th>
                      <th class="text-right">Peso</th>
                      <th class="text-center">Pç Cx</th>
                      <th class="text-center">Pç</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="etq in detalhesEtiquetas" :key="etq.ID" :class="statusValidade(etq.DATA_VALIDADE) === 'VENCIDA' ? 'table-danger-light' : 'table-success-light'">
                      <td class="font-weight-bold">{{ etq.PRODUTO_ID }}</td>
                      <td>{{ etq.PRODUTO }}</td>
                      <td class="text-center">{{ etq.CODIGO_BARRAS }}</td>
                      <td class="text-center font-weight-bold">
                        <span :class="statusValidade(etq.DATA_VALIDADE) === 'VENCIDA' ? 'text-danger' : 'text-success'">
                          {{ formatDataBR(etq.DATA_VALIDADE) }}
                        </span>
                      </td>
                      <td class="text-center">{{ etq.CENTRO_ESTOQUE || '-' }}</td>
                      <td class="text-right font-weight-bold">{{ formatNumero(etq.PESO) }}</td>
                      <td class="text-center">{{ etq.PECAS_CAIXA || 0 }}</td>
                      <td class="text-center">{{ etq.PECAS || 1 }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
  </div>
</template>

<script>
import Chart from 'chart.js'
import moment from 'moment'
import CecInputData from '../../components/Cec/CecInputData'
import RelatorioHTML from '../../utils/RelatorioHTML'
import tools from '../../tools/tools'

export default {
  name: 'EstoqueDashboard',
  components: {
    CecInputData
  },
  data() {
    return {
      carregando: false,
      totais: {
        TOTAL_PECAS: 0,
        TOTAL_PESO: 0,
        CUSTO_TOTAL: 0,
        TOTAL_ETIQUETAS: 0,
        ETIQUETAS_VENCIDAS: 0
      },
      filtroDataLimiteTimeline: moment().add(30, 'days').format('YYYY-MM-DD'),
      porGrupo: [],
      topProdutosPeso: [],
      validade: [],
      timelineValidade: [],
      tipoGraficoGrupo: 'PESO', // PESO ou PECAS
      charts: {
        grupo: null,
        validade: null,
        timeline: null,
        donutModal: null
      },
      modalTitulo: '',
      detalhesEtiquetas: [],
      detalhesResumo: {
        pesoTotal: 0,
        totalPecas: 0,
        itensVencidos: 0,
        custoTotal: 0
      }
    }
  },
  computed: {
    totalGeralPecas() { return this.porGrupo.reduce((acc, it) => acc + (it.PECAS || 0), 0) },
    totalGeralPeso() { return this.porGrupo.reduce((acc, it) => acc + (it.PESO || 0), 0) },
    totalGeralCusto() { return this.porGrupo.reduce((acc, it) => acc + (it.CUSTO || 0), 0) }
  },
  watch: {
    tipoGraficoGrupo() {
      this.atualizarGraficoGrupo()
    }
  },
  mounted() {
    this.carregarDados()
  },
  methods: {
    carregarDados() {
      this.carregando = true
      this.$http.get('/api/protected/estoque/dashboard', { 
        params: { dataLimiteTimeline: this.filtroDataLimiteTimeline } 
      })
        .then(res => {
          this.totais = res.data.totais || this.totais
          this.porGrupo = res.data.porGrupo || []
          this.topProdutosPeso = res.data.topProdutosPeso || []
          this.validade = res.data.validade || []
          this.timelineValidade = res.data.timelineValidade || []
          
          this.$nextTick(() => {
            this.renderizarGraficos()
          })
        })
        .finally(() => {
          this.carregando = false
        })
    },

    renderizarGraficos() {
      this.atualizarGraficoGrupo()
      this.atualizarGraficoValidade()
      this.atualizarGraficoTimeline()
    },

    atualizarGraficoGrupo() {
      if (this.charts.grupo) this.charts.grupo.destroy()

      const labels = this.porGrupo.map(it => it.GRUPO)
      const data = this.porGrupo.map(it => this.tipoGraficoGrupo === 'PESO' ? it.PESO : it.PECAS)
      const label = this.tipoGraficoGrupo === 'PESO' ? 'Peso (KG)' : 'Peças'

      const ctx = this.$refs.chartGrupo.getContext('2d')
      this.charts.grupo = new Chart(ctx, {
        type: 'bar',
        data: {
          labels,
          datasets: [{
            label,
            data,
            backgroundColor: 'rgba(54, 162, 235, 0.7)',
            borderColor: 'rgba(54, 162, 235, 1)',
            borderWidth: 1
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{ 
              ticks: { 
                beginAtZero: true,
                callback: (value) => value.toLocaleString('pt-BR')
              } 
            }]
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem, data) => {
                const val = tooltipItem.yLabel
                const label = data.datasets[tooltipItem.datasetIndex].label || ''
                return `${label}: ${val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              }
            }
          },
          plugins: {
            legend: { display: false }
          },
          onClick: (evt, item) => {
            if (item.length > 0) {
              const index = item[0]._index
              const grupo = labels[index]
              this.abrirDetalhesGrupo(grupo)
            }
          }
        }
      })
    },

    atualizarGraficoValidade() {
      if (this.charts.validade) this.charts.validade.destroy()

      const labels = this.validade.map(it => it.PRODUTO)
      const vencidas = this.validade.map(it => it.VENCIDAS)
      const validas = this.validade.map(it => it.VALIDAS)

      const ctx = this.$refs.chartValidade.getContext('2d')
      this.charts.validade = new Chart(ctx, {
        type: 'horizontalBar',
        data: {
          labels,
          datasets: [
            {
              label: 'Vencidas',
              data: vencidas,
              backgroundColor: 'rgba(255, 99, 132, 0.8)'
            },
            {
              label: 'Válidas',
              data: validas,
              backgroundColor: 'rgba(75, 192, 192, 0.8)'
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            xAxes: [{ stacked: true }],
            yAxes: [{ stacked: true }]
          },
          onClick: (evt, item) => {
            if (item.length > 0) {
              const index = item[0]._index
              const produto = labels[index]
              this.abrirDetalhesProduto(produto)
            }
          }
        }
      })
    },

    atualizarGraficoTimeline() {
      if (this.charts.timeline) this.charts.timeline.destroy()

      const labels = this.timelineValidade.map(it => moment(it.DATA).format('DD/MM'))
      const data = this.timelineValidade.map(it => it.TOTAL_PECAS)

      const ctx = this.$refs.chartTimeline.getContext('2d')
      this.charts.timeline = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{
            label: 'Peças (Volumes) vencendo',
            data,
            borderColor: '#f39c12',
            backgroundColor: 'rgba(243, 156, 18, 0.1)',
            fill: true,
            tension: 0.4
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            yAxes: [{ ticks: { beginAtZero: true } }]
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem) => `Vencimentos: ${tooltipItem.yLabel} etiquetas`
            }
          }
        }
      })
    },

    abrirDetalhesGrupo(grupo) {
      this.modalTitulo = `Etiquetas do Grupo: ${grupo}`
      this.buscarDetalhes({ grupo })
    },

    abrirDetalhesValidade(situacao) {
      this.modalTitulo = situacao === 'VENCIDA' ? 'Etiquetas Vencidas' : 'Etiquetas Válidas'
      this.buscarDetalhes({ situacaoValidade: situacao })
    },

    abrirDetalhesProduto(produto) {
      this.modalTitulo = `Produto: ${produto}`
      this.buscarDetalhes({ produto })
    },

    buscarDetalhes(params) {
      this.$bvModal.show('modal-detalhes-estoque')
      this.detalhesEtiquetas = []
      this.detalhesResumo = { pesoTotal: 0, totalPecas: 0, itensVencidos: 0, custoTotal: 0 }
      
      this.$http.get('/api/protected/estoque/dashboard/detalhes', { params })
        .then(res => {
          this.detalhesEtiquetas = res.data || []
          this.detalhesResumo.pesoTotal = this.detalhesEtiquetas.reduce((acc, it) => acc + (it.PESO || 0), 0)
          this.detalhesResumo.totalPecas = this.detalhesEtiquetas.reduce((acc, it) => acc + (it.PECAS || 0), 0)
          this.detalhesResumo.itensVencidos = this.detalhesEtiquetas.filter(it => this.statusValidade(it.DATA_VALIDADE) === 'VENCIDA').length
          this.detalhesResumo.custoTotal = this.detalhesEtiquetas.reduce((acc, it) => acc + ((it.VL_CUSTO || 0) * (it.PESO || 0)), 0)
          
          this.$nextTick(() => {
            this.atualizarGraficoDonutModal()
          })
        })
    },

    atualizarGraficoDonutModal() {
      if (this.charts.donutModal) this.charts.donutModal.destroy()
      
      const vencidas = this.detalhesEtiquetas.filter(it => this.statusValidade(it.DATA_VALIDADE) === 'VENCIDA').length
      const validas = this.detalhesEtiquetas.length - vencidas
      
      const ctx = this.$refs.chartDonutModal.getContext('2d')
      this.charts.donutModal = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['Válidas', 'Vencidas'],
          datasets: [{
            data: [validas, vencidas],
            backgroundColor: ['#28a745', '#dc3545'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: { position: 'bottom' },
          cutoutPercentage: 70
        }
      })
    },

    getCommonHeader(titulo) {
      const nomeUsuario = window.localStorage.getItem('nomeusuario') || 'SISTEMA'
      const nomeEmpresa = window.localStorage.getItem('nomeempresa') || 'CECWEB'
      const dataHora = moment().format('DD/MM/YYYY HH:mm')
      
      return `
        <div class="report-header">
          <div class="report-header-top">
            <div><strong>${nomeEmpresa}</strong></div>
            <div><strong>Usuário:</strong> ${nomeUsuario}</div>
          </div>
          <div class="report-header-title">${titulo}</div>
          <div class="report-header-sub">
            <div><strong>Emitido em:</strong> ${dataHora}</div>
            <div><strong>Filtro Timeline:</strong> Até ${moment(this.filtroDataLimiteTimeline).format('DD/MM/YYYY')}</div>
          </div>
        </div>
      `
    },

    getCommonFooter(totalItens) {
      return `
        <div class="report-footer">
          <div>CECFrigo - Inteligência em Gestão de Estoque</div>
          <div>Total de Registros: ${totalItens}</div>
          <div>Página 1 de 1</div>
        </div>
      `
    },

    getCommonStyles(landscape = false) {
      const orientation = landscape ? 'landscape' : 'portrait'
      return `
        <style>
          @page { size: A4 ${orientation}; margin: 15mm 12mm 12mm 15mm; }
          body { font-family: Arial, sans-serif; margin: 0; padding: 0; color: #111; font-size: 10px; }
          .report-header { border: 1px solid #000; margin-bottom: 10px; }
          .report-header-top, .report-header-sub { display: flex; justify-content: space-between; padding: 3px 6px; border-bottom: 1px solid #000; }
          .report-header-sub:last-child { border-bottom: none; }
          .report-header-title { padding: 5px; font-size: 12px; font-weight: bold; text-align: center; border-bottom: 1px solid #000; background: #f0f0f0; }
          .grid-main { width: 100%; border-collapse: collapse; margin-top: 5px; table-layout: fixed; }
          .grid-main th, .grid-main td { border: 1px solid #000; padding: 4px; vertical-align: middle; word-wrap: break-word; }
          .grid-main th { background: #eee; font-weight: bold; text-align: left; text-transform: uppercase; font-size: 9px; }
          .text-right { text-align: right; }
          .text-center { text-align: center; }
          .font-bold { font-weight: bold; }
          .grupo-row { background: #f5f5f5; font-weight: bold; }
          .total-row { font-weight: bold; background: #eee; }
          .report-footer { margin-top: 10px; display: flex; justify-content: space-between; font-size: 8px; border-top: 1px dashed #666; padding-top: 5px; }
          .chart-container-rep { text-align: center; margin: 20px 0; border: 1px solid #eee; padding: 10px; page-break-inside: avoid; }
          .chart-img { max-width: 100%; height: auto; max-height: 250px; }
          .kpi-grid-rep { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; margin-bottom: 15px; }
          .kpi-box-rep { border: 1px solid #000; padding: 8px; text-align: center; }
          .kpi-box-rep small { display: block; font-size: 8px; text-transform: uppercase; margin-bottom: 3px; }
          .kpi-box-rep strong { font-size: 14px; }
        </style>
      `
    },

    formatBRL(v) { return (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) },
    formatNumero(v) { return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) },
    formatInteiro(v) { return (v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 0 }) },
    formatDataBR(v) { return v ? moment(v).format('DD/MM/YYYY') : '-' },
    statusValidade(dt) { return moment(dt).isBefore(moment()) ? 'VENCIDA' : 'VALIDA' },

    async imprimirResumoGeral() {
      const empresa = await this.obterEmpresaPreferencial()
      const titulo = 'RELATÓRIO ESTRATÉGICO DE ESTOQUE'
      
      // Capturar gráficos
      const imgGrupo = this.$refs.chartGrupo.toDataURL('image/png')
      const imgTimeline = this.$refs.chartTimeline.toDataURL('image/png')
      
      let rowsHtml = ''
      this.porGrupo.forEach(g => {
        rowsHtml += `
          <tr class="grupo-row">
            <td>${g.GRUPO}</td>
            <td class="text-center">${this.formatInteiro(g.PECAS)}</td>
            <td class="text-right">${this.formatNumero(g.PESO)}</td>
            <td class="text-right">${this.formatBRL(g.CUSTO)}</td>
          </tr>
        `
        this.topProdutosPeso.filter(p => p.GRUPO === g.GRUPO).forEach(p => {
          rowsHtml += `
            <tr>
              <td style="padding-left: 20px;">${p.PRODUTO}</td>
              <td class="text-center">${this.formatInteiro(p.PECAS)}</td>
              <td class="text-right">${this.formatNumero(p.PESO)}</td>
              <td class="text-right">${this.formatBRL(p.CUSTO)}</td>
            </tr>
          `
        })
      })

      const html = `
        <!doctype html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${titulo}</title>
          ${this.getCommonStyles(false)}
        </head>
        <body>
          ${this.getCommonHeader(titulo)}
          
          <div class="kpi-grid-rep">
            <div class="kpi-box-rep"><small>Peso Total</small><strong>${this.formatNumero(this.totais.TOTAL_PESO)} KG</strong></div>
            <div class="kpi-box-rep"><small>Total Peças</small><strong>${this.formatInteiro(this.totais.TOTAL_PECAS)}</strong></div>
            <div class="kpi-box-rep"><small>Custo Total</small><strong>${this.formatBRL(this.totais.CUSTO_TOTAL)}</strong></div>
            <div class="kpi-box-rep"><small>Itens Vencidos</small><strong>${this.formatInteiro(this.totais.ETIQUETAS_VENCIDAS)}</strong></div>
          </div>

          <div style="display: flex; gap: 10px; margin-bottom: 20px;">
            <div class="chart-container-rep" style="flex: 1;">
              <div style="font-weight: bold; margin-bottom: 5px;">Distribuição por Grupo</div>
              <img src="${imgGrupo}" class="chart-img">
            </div>
            <div class="chart-container-rep" style="flex: 1;">
              <div style="font-weight: bold; margin-bottom: 5px;">Cronograma de Vencimento</div>
              <img src="${imgTimeline}" class="chart-img">
            </div>
          </div>

          <table class="grid-main">
            <thead>
              <tr>
                <th>Grupo / Produto</th>
                <th class="text-center" width="80">Peças</th>
                <th class="text-right" width="100">Peso (KG)</th>
                <th class="text-right" width="120">Custo Est.</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
              <tr class="total-row">
                <td>TOTAL GERAL</td>
                <td class="text-center">${this.formatInteiro(this.totalGeralPecas)}</td>
                <td class="text-right">${this.formatNumero(this.totalGeralPeso)}</td>
                <td class="text-right">${this.formatBRL(this.totalGeralCusto)}</td>
              </tr>
            </tbody>
          </table>

          ${this.getCommonFooter(this.topProdutosPeso.length)}
        </body>
        </html>
      `
      RelatorioHTML.imprimir(html, titulo, 'A4', 'portrait')
    },

    async obterEmpresaPreferencial() {
      try {
        const { data } = await this.$http.get('/api/protected/empresa/getPreferencial')
        return Array.isArray(data) ? data[0] : data
      } catch (e) {
        return { FANTASIA: 'CECWEB', RAZAO: 'CECWEB SISTEMA' }
      }
    },

    imprimirDetalhes() {
      const titulo = `DETALHAMENTO: ${this.modalTitulo}`
      const imgDonut = this.$refs.chartDonutModal.toDataURL('image/png')
      
      const rowsHtml = this.detalhesEtiquetas.map(it => `
        <tr style="background-color: ${this.statusValidade(it.DATA_VALIDADE) === 'VENCIDA' ? '#ffebee' : '#f1f8e9'}">
          <td class="text-center">${it.PRODUTO_ID}</td>
          <td>${it.PRODUTO}</td>
          <td class="text-center">${it.CODIGO_BARRAS}</td>
          <td class="text-center font-bold">${this.formatDataBR(it.DATA_VALIDADE)}</td>
          <td class="text-center">${it.CENTRO_ESTOQUE || '-'}</td>
          <td class="text-right font-bold">${this.formatNumero(it.PESO)}</td>
          <td class="text-center">${it.PECAS_CAIXA || 0}</td>
          <td class="text-center">${it.PECAS || 1}</td>
        </tr>
      `).join('')

      const html = `
        <!doctype html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${titulo}</title>
          ${this.getCommonStyles(false)}
        </head>
        <body>
          ${this.getCommonHeader(titulo)}
          
          <div class="kpi-grid-rep">
            <div class="kpi-box-rep"><small>Peso</small><strong>${this.formatNumero(this.detalhesResumo.pesoTotal)} KG</strong></div>
            <div class="kpi-box-rep"><small>Peças</small><strong>${this.formatInteiro(this.detalhesResumo.totalPecas)}</strong></div>
            <div class="kpi-box-rep"><small>Vencidos</small><strong>${this.formatInteiro(this.detalhesResumo.itensVencidos)}</strong></div>
            <div class="kpi-box-rep"><small>Valor Est.</small><strong>${this.formatBRL(this.detalhesResumo.custoTotal)}</strong></div>
          </div>

          <div class="chart-container-rep">
            <div style="font-weight: bold; margin-bottom: 5px;">Distribuição de Validade</div>
            <img src="${imgDonut}" class="chart-img" style="max-height: 150px;">
          </div>

          <table class="grid-main">
            <thead>
              <tr>
                <th width="50" class="text-center">Cód</th>
                <th>Produto</th>
                <th class="text-center" width="90">Barras</th>
                <th class="text-center" width="70">Validade</th>
                <th class="text-center">Centro</th>
                <th class="text-right" width="60">Peso</th>
                <th class="text-center" width="40">Cx</th>
                <th class="text-center" width="40">Pç</th>
              </tr>
            </thead>
            <tbody>${rowsHtml}</tbody>
          </table>

          ${this.getCommonFooter(this.detalhesEtiquetas.length)}
        </body>
        </html>
      `
      RelatorioHTML.imprimir(html, titulo, 'A4', 'portrait')
    }
  }
}
</script>

<style scoped>
.vibrant-card {
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  border-radius: 15px;
  padding: 1.5rem;
  color: white;
  position: relative;
  overflow: hidden;
}

.vibrant-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.2) !important;
}

.card-v-lucro { background: linear-gradient(135deg, #4b6cb7 0%, #182848 100%); }
.card-v-receber { background: linear-gradient(135deg, #00b09b 0%, #96c93d 100%); }
.card-v-recebido { background: linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%); }
.card-v-pago { background: linear-gradient(135deg, #cb2d3e 0%, #ef473a 100%); }

.vibrant-card__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.vibrant-card__value {
  font-size: 1.8rem;
  font-weight: 800;
  margin-top: 0.5rem;
}

.bg-soft-gray { background-color: #f8fafc; }
.table-danger-light { background-color: #fff1f2 !important; }
.table-success-light { background-color: #f0fdf4 !important; }

.modern-premium-modal {
  border-radius: 16px;
  overflow: hidden;
}

.report-header-premium {
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #fff;
}

.kpi-card-premium {
  background: #fff;
  padding: 1.25rem;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.kpi-card-premium small { display: block; text-transform: uppercase; font-weight: 700; color: #94a3b8; font-size: 0.65rem; margin-bottom: 0.5rem; }
.kpi-card-premium h3 { font-weight: 800; margin: 0; font-size: 1.5rem; letter-spacing: -0.5px; }

.clickable-row { cursor: pointer; transition: background 0.2s; }
.clickable-row:hover { background-color: #f1f5f9 !important; }

.gap-3 { gap: 1rem; }
</style>



// WEBPACK FOOTER //
// src/views/estoque/EstoqueDashboard.vue